import React, { useState } from "react";
import { Routes, Route, NavLink, useParams } from "react-router-dom";
import { LayoutDashboard, Users, Zap, Clock, LogOut, Menu, X, Wallet, User, MessageSquare } from "lucide-react";
import { logoutAndRedirect, getActiveSubdomain } from "@/lib/api";
import { useFCM } from "@/hooks/useFCM";
import DealerDashboard from "./DealerDashboard";
import MyCustomers from "./MyCustomers";
import InitiateRecharge from "./InitiateRecharge";
import DealerHistory from "./DealerHistory";
import DealerWallet from "./DealerWallet";
import DealerProfile from "./DealerProfile";
import DealerComplaints from "./Complaints";

export default function DealerPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { subdomain: paramSubdomain } = useParams();
  const activeSubdomain = getActiveSubdomain();
  const subdomain = activeSubdomain || paramSubdomain;
  const base = activeSubdomain ? "/dealer" : `/site/${subdomain}/dealer`;
  const logout = () => { logoutAndRedirect(); };

  // Auto-register FCM push token
  useFCM({ autoRequest: true, showToasts: true });

  const nav = [
    { to: base,               label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: `${base}/customers`, label: "Customers", icon: Users },
    { to: `${base}/recharge`,  label: "Recharge",  icon: Zap },
    { to: `${base}/wallet`,    label: "Wallet",    icon: Wallet },
    { to: `${base}/history`,   label: "History",   icon: Clock },
    { to: `${base}/complaints`, label: "Complaints", icon: MessageSquare },
    { to: `${base}/profile`,   label: "Profile",   icon: User },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-body">
      {/* Top Nav */}
      <nav className="fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <span className="font-heading font-bold text-brand-nav text-lg">Dealer Panel</span>
          <button onClick={() => setMenuOpen(v => !v)} className="p-2 rounded-lg hover:bg-slate-100 md:hidden">
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="hidden md:flex items-center gap-1">
            {nav.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-brand-primary/10 text-brand-primary" : "text-slate-600 hover:bg-slate-100"}`
                }>
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
            ))}
            <button onClick={logout} className="ml-4 flex items-center gap-1.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden px-4 pb-3 border-t border-slate-100 space-y-1">
            {nav.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-brand-primary/10 text-brand-primary" : "text-slate-600"}`
                }>
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
            ))}
            <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-500 rounded-lg">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        )}
      </nav>

      {/* Content */}
      <div className="pt-16 pb-8 max-w-4xl mx-auto px-4">
        <Routes>
          <Route index element={<DealerDashboard />} />
          <Route path="customers" element={<MyCustomers />} />
          <Route path="recharge" element={<InitiateRecharge />} />
          <Route path="wallet" element={<DealerWallet />} />
          <Route path="history" element={<DealerHistory />} />
          <Route path="complaints" element={<DealerComplaints />} />
          <Route path="profile" element={<DealerProfile />} />
        </Routes>
      </div>
    </div>
  );
}
